<template>
  <v-dialog v-if="allowed && value" :value="value" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="900" scrollable
            :persistent="busy" @input="$emit('input', $event)">
    <v-card class="dc-templates" data-testid="templates-editor" :aria-busy="loading || busy">
      <v-card-title>{{ $t('damage_control.templates.title') }}</v-card-title>
      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate />
        <v-alert v-if="errorText" type="warning" outlined role="alert">{{ errorText }}</v-alert>
        <v-alert v-if="saved" type="success" dense text role="status">{{ $t('damage_control.templates.saved') }}</v-alert>
        <v-btn v-if="!loaded && !loading" text @click="load">{{ $t('custom.update') }}</v-btn>
        <template v-if="loaded">
          <section v-if="standard" data-testid="standard-template" class="mb-6">
            <h3 class="text-h6">{{ $t('damage_control.templates.standard') }}</h3>
            <ol class="pl-5">
              <li v-for="zone in standardZones" :key="zone.key" class="my-3">
                <strong>{{ zone.title }}</strong>
                <span class="ml-2">{{ $t(zone.required ? 'damage_control.templates.required' : 'damage_control.templates.optional') }}</span>
                <p v-if="zone.hint" class="mb-0">{{ zone.hint }}</p>
              </li>
            </ol>
          </section>
          <v-alert v-else type="info" outlined>{{ $t('damage_control.errors.damage_control_no_template') }}</v-alert>
          <section v-if="template || standard">
            <h3 class="text-h6 mb-3">{{ $t('damage_control.templates.branch') }}</h3>
            <v-text-field v-model="name" :disabled="busy" :label="$t('damage_control.templates.name')" data-testid="template-name" @input="saved = false" />
            <v-btn v-if="!template" color="primary" :disabled="busy" data-testid="copy-template" @click="copy">
              {{ $t('damage_control.templates.customize') }}
            </v-btn>
            <template v-else>
              <article v-for="(zone, index) in zones" :key="zone.key" class="dc-template-zone pa-3 mb-4">
                <v-text-field v-model="zone.title" :disabled="busy" :label="$t('damage_control.templates.zone_title')" :data-testid="`zone-title-${zone.key}`" @input="saved = false" />
                <v-text-field v-model="zone.hint" :disabled="busy" :label="$t('damage_control.templates.hint')" :data-testid="`zone-hint-${zone.key}`" @input="saved = false" />
                <v-switch v-model="zone.required" :disabled="busy" :label="$t('damage_control.templates.required')" :data-testid="`zone-required-${zone.key}`" class="mt-0" @change="saved = false" />
                <div class="dc-template-actions mb-3">
                  <v-btn outlined small :disabled="busy || index === 0" :aria-label="$t('damage_control.templates.up') + ': ' + zone.title" :data-testid="`move-up-${zone.key}`" @click="move(index, -1)">{{ $t('damage_control.templates.up') }}</v-btn>
                  <v-btn outlined small :disabled="busy || index === zones.length - 1" :aria-label="$t('damage_control.templates.down') + ': ' + zone.title" :data-testid="`move-down-${zone.key}`" @click="move(index, 1)">{{ $t('damage_control.templates.down') }}</v-btn>
                  <v-btn text small color="error" :disabled="busy" :aria-label="$t('damage_control.templates.remove_zone') + ': ' + zone.title" :data-testid="`remove-zone-${zone.key}`" @click="removeZone(index)">{{ $t('damage_control.templates.remove_zone') }}</v-btn>
                </div>
                <img v-if="zone.example_photo_url" :src="zone.example_photo_url" :alt="$t('damage_control.templates.example') + ': ' + zone.title" :data-testid="`example-${zone.key}`" class="dc-template-example" />
                <v-file-input :value="null" accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif" :disabled="busy || !persistedZone(zone)"
                              :loading="uploadingKey === zone.key" :label="$t('damage_control.templates.attach_example')" :hint="$t('damage_control.templates.photo_formats')" persistent-hint
                              :data-testid="`example-file-${zone.key}`" @change="uploadExample(zone, $event)" />
                <p v-if="!persistedZone(zone)" class="text-caption mt-1">{{ $t('damage_control.templates.save_before_photo') }}</p>
                <v-btn v-if="zone.example_photo_url || zone.example_photo_upload_token" text small :disabled="busy" :data-testid="`remove-example-${zone.key}`" @click="removeExample(zone)">{{ $t('damage_control.templates.remove_example') }}</v-btn>
              </article>
              <v-text-field v-model="newTitle" :disabled="busy" :label="$t('damage_control.templates.zone_title')" data-testid="new-zone-title" @keydown.enter.prevent="addZone" />
              <v-btn outlined :disabled="busy || !newTitle.trim()" data-testid="add-zone" @click="addZone">{{ $t('damage_control.templates.add_zone') }}</v-btn>
            </template>
          </section>
        </template>
      </v-card-text>
      <v-card-actions class="dc-template-actions pa-4">
        <v-btn v-if="template" color="primary" :disabled="loading || busy" data-testid="save-template" @click="save">{{ $t('custom.save') }}</v-btn>
        <v-btn v-if="template && standard" text :disabled="loading || busy" data-testid="reset-template" @click="reset">{{ $t('damage_control.templates.reset') }}</v-btn>
        <v-spacer />
        <v-btn text :disabled="busy" @click="$emit('input', false)">{{ $t('custom.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { damageControlApi, damageControlEnabled, reportDamageControlFailure } from '@/api/damageControl';
import damageControlAccessMixin from '@/mixins/damageControlAccessMixin';
import roleMixin from '@/mixins/roleMixin';
import { prepareExamplePhoto } from './examplePhoto';
import { uploadTarget } from '@/services/damageControlShots';

const ordered = zones => [...(zones || [])].sort((a, b) => a.position - b.position);
export default {
  name: 'TemplatesDialog',
  mixins: [roleMixin, damageControlAccessMixin],
  props: { value: { type: Boolean, default: false } },
  data: () => ({ standard: null, template: null, name: '', zones: [], newTitle: '',
    loading: false, loaded: false, busy: false, saved: false, errorText: '', uploadingKey: null,
    sequence: 0, disposed: false, previews: [] }),
  computed: {
    company() { return this.damageControlCompany || {}; },
    allowed() { return this.company.id != null && damageControlEnabled(this.company) && this.isManagerPlus; },
    contextKey() { return JSON.stringify([this.company.id, this.company.rental_object, this.allowed, this.value]); },
    standardZones() { return ordered(this.standard && this.standard.zones); },
  },
  watch: { contextKey: { immediate: true, handler() { this.load(); } } },
  beforeDestroy() { this.disposed = true; this.sequence += 1; this.releasePreviews(); },
  methods: {
    current(sequence) { return !this.disposed && this.allowed && this.value && sequence === this.sequence; },
    canEdit() { return this.allowed && this.value && this.loaded && !this.loading && !this.busy && !this.disposed; },
    releasePreviews() { this.previews.forEach(url => URL.revokeObjectURL(url)); this.previews = []; },
    releasePreview(zone) {
      if (!this.previews.includes(zone.example_photo_url)) return;
      URL.revokeObjectURL(zone.example_photo_url);
      this.previews = this.previews.filter(url => url !== zone.example_photo_url);
    },
    applyTemplate(template) {
      this.releasePreviews();
      this.template = template;
      this.name = template ? template.name : this.$t('damage_control.templates.branch');
      this.zones = template ? ordered(template.zones).map(zone => ({ ...zone })) : [];
      this.newTitle = '';
    },
    async load() {
      const sequence = ++this.sequence;
      this.applyTemplate(null); this.standard = null; this.loaded = false; this.loading = false;
      this.busy = false; this.uploadingKey = null; this.errorText = ''; this.saved = false;
      if (!this.current(sequence)) return;
      this.loading = true;
      try {
        const data = await damageControlApi.listTemplates(this.axios);
        if (!this.current(sequence)) return;
        this.standard = (data.standard || []).find(item => item.rental_object === this.company.rental_object) || null;
        this.applyTemplate((data.templates || []).find(item => item.rental_object === this.company.rental_object) || null);
        this.loaded = true;
      } catch (error) {
        if (this.current(sequence)) this.errorText = reportDamageControlFailure(this, error, { silent: true });
      } finally { if (this.current(sequence)) this.loading = false; }
    },
    invalid() { this.errorText = this.$t('damage_control.errors.validation_failed'); this.saved = false; },
    valid(zones) {
      return typeof this.name === 'string' && !!this.name.trim() && zones.length > 0 &&
        new Set(zones.map(zone => zone.key)).size === zones.length && zones.every(zone =>
          typeof zone.key === 'string' && /^[a-z][a-z0-9_]*$/.test(zone.key) && zone.key === zone.key.trim() &&
          typeof zone.title === 'string' && !!zone.title.trim() &&
          (zone.hint == null || typeof zone.hint === 'string') && typeof zone.required === 'boolean');
    },
    payloadZones() {
      return this.zones.map((zone, position) => {
        const result = { key: zone.key, title: zone.title, hint: zone.hint, required: zone.required, position };
        if (zone.example_photo_upload_token) result.example_photo_upload_token = zone.example_photo_upload_token;
        else if (zone.example_photo === null) result.example_photo = null;
        return result;
      });
    },
    async mutate(operation) {
      const sequence = this.sequence;
      this.busy = true; this.errorText = ''; this.saved = false;
      try {
        const template = await operation();
        if (!this.current(sequence) || !template) return;
        this.applyTemplate(template); this.saved = true;
      } catch (error) {
        if (this.current(sequence)) this.errorText = reportDamageControlFailure(this, error, { silent: true });
      } finally { if (this.current(sequence)) this.busy = false; }
    },
    async copy() {
      if (!this.canEdit() || this.template || !this.standard) return;
      if (!this.name.trim()) { this.invalid(); return; }
      await this.mutate(() => damageControlApi.createTemplate(this.axios, { rental_object: this.company.rental_object, name: this.name }));
    },
    async save() {
      if (!this.canEdit() || !this.template) return;
      const zones = this.payloadZones();
      if (!this.valid(zones)) { this.invalid(); return; }
      await this.mutate(() => damageControlApi.updateTemplate(this.axios, this.template.id, { name: this.name, zones }));
    },
    async reset() {
      if (!this.canEdit() || !this.template || !this.standard) return;
      const sequence = this.sequence, id = this.template.id;
      await this.mutate(async () => {
        const confirmed = await this.$confirm({ title: this.$t('damage_control.templates.reset'),
          text: this.$t('damage_control.templates.confirm_reset'),
          confirmButtonText: this.$t('custom.yes'), cancelButtonText: this.$t('custom.no') });
        if (!confirmed || !this.current(sequence)) return null;
        return damageControlApi.resetTemplate(this.axios, id);
      });
    },
    addZone() {
      if (!this.canEdit() || !this.template || !this.newTitle.trim()) return;
      // Keep keys stable, including removed persisted zones: their evidence must not be reused.
      const keys = new Set([...this.zones, ...(this.template.zones || [])].map(zone => zone.key));
      let number = 1;
      while (keys.has(`zone_${number}`)) number += 1;
      this.zones.push({ key: `zone_${number}`, title: this.newTitle.trim(), hint: '', required: true });
      this.newTitle = ''; this.saved = false;
    },
    move(index, direction) {
      const target = index + direction;
      if (!this.canEdit() || target < 0 || target >= this.zones.length) return;
      const [zone] = this.zones.splice(index, 1); this.zones.splice(target, 0, zone); this.saved = false;
    },
    removeZone(index) {
      if (!this.canEdit() || !this.zones[index]) return;
      this.releasePreview(this.zones[index]); this.zones.splice(index, 1); this.saved = false;
    },
    persistedZone(zone) { return !!this.template && (this.template.zones || []).some(item => item.key === zone.key); },
    removeExample(zone) {
      if (!this.canEdit() || !this.zones.includes(zone)) return;
      this.releasePreview(zone);
      this.$delete(zone, 'example_photo_upload_token');
      this.$set(zone, 'example_photo', null); this.$set(zone, 'example_photo_url', null); this.saved = false;
    },
    async uploadExample(zone, file) {
      if (!file || !this.canEdit() || !this.zones.includes(zone) || !this.persistedZone(zone)) return;
      const sequence = this.sequence, id = this.template.id;
      this.busy = true; this.uploadingKey = zone.key; this.errorText = ''; this.saved = false;
      try {
        let image;
        try { image = await prepareExamplePhoto(file); }
        catch (_error) {
          if (this.current(sequence)) this.errorText = this.$t('damage_control.templates.photo_invalid');
          return;
        }
        if (!this.current(sequence)) return;
        const prepared = await damageControlApi.templateExamplePhotoUpload(this.axios, id, { zone_key: zone.key, content_type: image.type, size: image.size });
        if (!this.current(sequence)) return;
        await uploadTarget(prepared.upload, image, fetch, undefined, "Example upload failed");
        if (!prepared.upload_token) throw new Error('Example upload failed');
        if (!this.current(sequence)) return;
        const url = URL.createObjectURL(image);
        this.releasePreview(zone); this.previews.push(url);
        this.$delete(zone, 'example_photo');
        this.$set(zone, 'example_photo_upload_token', prepared.upload_token);
        this.$set(zone, 'example_photo_url', url);
      } catch (error) {
        if (this.current(sequence)) this.errorText = reportDamageControlFailure(this, error, { silent: true });
      } finally {
        if (this.current(sequence)) { this.busy = false; this.uploadingKey = null; }
      }
    },
  },
};
</script>

<style scoped>
.dc-templates { min-width: 0; }
.dc-templates .v-card__title { word-break: normal; overflow-wrap: anywhere; }
.dc-template-zone { border: 1px solid rgba(128, 128, 128, .35); border-radius: 4px; }
.dc-template-example { display: block; max-width: 100%; max-height: 240px; object-fit: contain; }
.dc-template-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.dc-templates .v-btn { max-width: 100%; height: auto; min-height: 36px; padding-top: 8px; padding-bottom: 8px; }
.dc-templates ::v-deep .v-btn__content { white-space: normal; }
.dc-templates ::v-deep .v-input { min-width: 0; }
.dc-templates li { overflow-wrap: anywhere; }
@media (max-width: 600px) {
  .dc-templates > .v-card__text { padding: 12px; }
  .dc-template-actions.v-card__actions .v-btn { margin-left: 0; }
}
</style>
